import * as React from 'react';
import {
  Box, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, InputAdornment, Typography, Stack, Paper, TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import Data from '../Data/Data.json';
import { Crypter } from '../Functions/Security/Crypter';
import { useAuthHeader } from '../Services/Auth/sessionAuth';
import SweetAlert from 'sweetalert2';

export interface CodeLookupFieldProps {
  label: string;
  value?: string;                 // code sélectionné
  libelle?: string;               // libellé affiché
  onChange: (code: string, libelle: string) => void;
  // Paramètres de recherche (équivalents à ParamsPicklist)
  table: string;
  colonne1: string;               // colonne "code"
  colonne2: string;               // colonne "libellé"
  colagence?: string;
  codeAgence?: string;
  choix?: number;                 // défaut: 22 (recherche standard)
  colfiltre?: string;
  valfiltre?: string;
  // UI
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
}

interface LookupRow { key: number; Code: string; Libelle: string; }

export default function CodeLookupField({
  label, value = '', libelle = '', onChange,
  table, colonne1, colonne2, colagence, codeAgence,
  choix = 22, colfiltre, valfiltre,
  disabled = false, required = false, placeholder,
}: CodeLookupFieldProps) {
  const authHeader = useAuthHeader();
  const [open, setOpen] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const [rows, setRows] = React.useState<LookupRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedKey, setSelectedKey] = React.useState<number | null>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const searchApi = async (init: string): Promise<LookupRow[]> => {
    const data = {
      CodeAgence: codeAgence,
      Table: table,
      Colonne1: colonne1,
      Colonne2: colonne2,
      Colagence: colagence,
      Init: init,
      choix: choix,
      colfiltre,
      valfiltre,
    };
    const config = {
      method: 'get' as const,
      maxBodyLength: Infinity,
      url: Data.ApiUrl.AnySearch,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader(),
        'CypherSearch': Crypter.encrypt(JSON.stringify(data)),
      },
    };
    const response = await axios(config);
    if (!response.data) return [];
    const res = JSON.parse(Crypter.decrypt(response.data)).Value;
    if (res.StatutCode !== 200) return [];
    const datas = res.Result?.Table || [];
    // Priorité aux noms de colonnes (colonne1/colonne2), sinon 1re/2e colonne
    return datas.map((row: any, i: number) => {
      const keys = Object.keys(row);
      return {
        key: i,
        Code: String(row[colonne1] ?? row[keys[0]] ?? ''),
        Libelle: String(row[colonne2] ?? row[keys[1]] ?? ''),
      };
    });
  };

  const applySelect = (row: LookupRow) => {
    setSelectedKey(row.key);
    onChange(row.Code, row.Libelle);
    setTimeout(() => { setOpen(false); setRows([]); setSearchText(''); setSelectedKey(null); }, 200);
  };

  const handleSearch = async () => {
    setLoading(true);
    setPage(0);
    try {
      // Recherche vide : Init vide -> toutes les données (comme SearchModal)
      const res = await searchApi(searchText.trim());
      setRows(res);
      if (res.length === 1) applySelect(res[0]);     // auto-sélection si un seul résultat
    } catch (err: any) {
      SweetAlert.fire({ title: 'Erreur', text: err.message, icon: 'error' });
    } finally { setLoading(false); }
  };

  const handleOpen = async () => {
    if (disabled) return;
    setOpen(true);
    setSelectedKey(null);
    setPage(0);
    if (value) {
      setSearchText(value);
      setLoading(true);
      try { setRows(await searchApi(value)); } catch { /* silent */ }
      finally { setLoading(false); }
    } else {
      setSearchText('');
      setRows([]);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSearchText('');
    setRows([]);
    setSelectedKey(null);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', '');
  };

  return (
    <>
      <TextField
        label={label}
        size="small"
        fullWidth
        required={required}
        disabled={disabled}
        value={value}
        placeholder={placeholder || 'Cliquez pour rechercher...'}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          sx: { cursor: 'pointer' },
          endAdornment: (
            <InputAdornment position="end">
              {value && !disabled && (
                <IconButton size="small" edge="end" onClick={handleClear} title="Effacer">
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" edge="end" onClick={handleOpen} disabled={disabled}>
                <SearchIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        // Libellé affiché uniquement si présent : pas d'espace réservé,
        // le champ garde exactement la hauteur des autres filtres
        helperText={libelle || undefined}
        FormHelperTextProps={{ sx: { mx: 0.5, mt: 0, lineHeight: 1.2, fontSize: 10.5, color: '#64748b' } }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary">{label}</Typography>
          <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Rechercher par code ou libellé..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <Button variant="contained" onClick={handleSearch} disabled={loading} sx={{ borderRadius: '10px', textTransform: 'none' }}>
              Rechercher
            </Button>
          </Stack>

          <Paper variant="outlined" sx={{ borderRadius: '10px', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 360 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
              ) : rows.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8', fontSize: 13 }}>
                  Aucun résultat. Lancez une recherche.
                </Box>
              ) : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, width: 140 }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Libellé</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                      <TableRow
                        key={row.key}
                        hover
                        selected={selectedKey === row.key}
                        onClick={() => applySelect(row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>{row.Code}</TableCell>
                        <TableCell>{row.Libelle}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
            {!loading && rows.length > 0 && (
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Lignes par page :"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
              />
            )}
          </Paper>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
